import produce, { Draft } from 'immer';

import { Reducer, WidgetBlock, WidgetBlocksState, WidgetState } from '../WidgetBlocks/types/IWidgetBlock';
import {
    CustomEventAction,
    DataLoadedAction,
    ErrorLoadingDataAction,
    ErrorLoadingTemplateAction,
    InitializeWidgetAction,
    LoadedPersistedStateAction,
    LoadingDataAction,
    LoadingTemplateAction,
    NotificationDismissedAction,
    ReportSiteAction,
    ReportTimeAction,
    SetCapabilitiesLoadedAction,
    SetTimeAction,
    SetTimeForAllWidgetBlocksAction,
    TemplateLoadedAction,
    TypeKeys,
    WidgetBlockAction,
} from './widgetblock/actions/types';

/**
 * TODO: We currently store the reducers as part of the redux state.
 * This is bad practice and should not be done. Arguably this class should just be a HoF for the reducers.
 *
 */
const addReducers = (widgetBlocks: WidgetBlock[] = []) =>
    widgetBlocks.reduce<Map<string, WidgetBlock['reducer']>>((acc, { type, reducer }) => {
        if (reducer) {
            acc.set(type, reducer);
        }

        return acc;
    }, new Map());

type Actions =
    | LoadingDataAction
    | DataLoadedAction<any>
    | ErrorLoadingDataAction
    | ErrorLoadingTemplateAction
    | CustomEventAction<any>
    | InitializeWidgetAction
    | LoadedPersistedStateAction
    | LoadingTemplateAction
    | NotificationDismissedAction
    | TemplateLoadedAction<any>
    | ReportSiteAction<{ site: any }>
    | ReportTimeAction
    | SetCapabilitiesLoadedAction
    | SetTimeAction
    | SetTimeForAllWidgetBlocksAction;

const buildReducerFunction = (widgetBlocks: WidgetBlock[]) => {
    const reducers = addReducers(widgetBlocks);

    return (state: WidgetBlocksState = { reducers, widgetModels: new Map() }, action: Actions) => {
        /**
         * The custom event has the ability to be called `CUSTOM_EVENT_${widgetId}_${eventName}`.
         * This is so that when we are viewing these while debugging, we can discern what is happening.
         * However, all the WidgetBlocks expect that the CUSTOM_EVENT action will be called CUSTOM_EVENT.
         * So we rename them back to the generic CUSTOM_EVENT in here.
         * This is **very** bad practice, and we should not do it.
         *
         * In essence, this was a hack we put in for debugging purposes.
         * Ideally, we should fix this by either allowing CUSTOM_EVENT_X, always using the debugging CUSTOM_EVENT format, or something else.
         *
         * Issue #183 tracks.
         */
        let { type: actionType } = action;

        if (actionType.startsWith(TypeKeys.CUSTOM_EVENT)) {
            actionType = TypeKeys.CUSTOM_EVENT;
        }

        switch (actionType) {
            case TypeKeys.LOADING_DATA:
            case TypeKeys.DATA_LOADED:
            case TypeKeys.ERROR_LOADING_DATA:
            case TypeKeys.LOADING_TEMPLATE:
            case TypeKeys.CUSTOM_EVENT:
            case TypeKeys.TEMPLATE_LOADED:
            case TypeKeys.SET_REPORT_TIME:
            case TypeKeys.SET_TIME:
            case TypeKeys.SET_REPORT_SITE:
            case TypeKeys.CAPABILITIES_LOADED:
            case TypeKeys.NOTIFICATION_DISMISSED:
            case TypeKeys.LOADED_PERSISTED_STATE:
            case TypeKeys.ERROR_LOADING_TEMPLATE: {
                return produce(state, (draft) => {
                    updateWidgetStateByRunningReducerOnAction(draft, { ...action, type: actionType } as WidgetBlockAction);
                });
            }

            case TypeKeys.SET_TIME_FOR_ALL_WIDGET_BLOCKS: {
                const { widgetModels } = state;

                Array.from(widgetModels).forEach(([widgetId, widgetInstances]) => {
                    Array.from(widgetInstances).forEach(([instanceId]) => {
                        state = produce(state, (draft) => {
                            updateWidgetStateByRunningReducerOnAction(draft, {
                                type: TypeKeys.SET_TIME,
                                payload: {
                                    ...action.payload,
                                    widgetId,
                                    instanceId,
                                },
                            });
                        });
                    });
                });

                return state;
            }

            default: {
                return state;
            }
        }
    };
};

const updateWidgetStateByRunningReducerOnAction = (state: Draft<WidgetBlocksState>, action: WidgetBlockAction) => {
    const { widgetId, instanceId } = action.payload;
    const { reducers } = state;

    if (widgetId && instanceId) {
        const reducer: Reducer<WidgetState> = reducers.get(widgetId) || ((state) => state);

        const widgets = state.widgetModels.get(widgetId);

        if (!widgets) {
            state.widgetModels.set(widgetId, new Map());
        }

        const widgetState = state.widgetModels.get(widgetId)?.get(instanceId);

        state.widgetModels.get(widgetId)!.set(instanceId, updateStateUsingReducer(reducer, action)(widgetState));
    }
};

const updateStateUsingReducer = (reducer: Reducer<WidgetState>, action: WidgetBlockAction) => (currentState?: WidgetState) => reducer(currentState, action);

export default buildReducerFunction;
export { Actions };
